import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { appConfig as config } from 'roc-package-web-app-react/app/shared/universal-config';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { themer } from '@spp/theme-provider';
import { reduxActions as trackingActions } from '@spp/tracking';
import { provideHooks } from 'redial';
import throttle from 'lodash.throttle';
import { PageOptions } from '@spp/ad-components';
import deferOnload from '../../utils/defer-onload';
import { HorseshoeAd, SponsorAd } from '../../local-components/Ads';
import BodyClass from '../../local-components/BodyClass';
import {
    fetchCollectionArticlesAction,
    addCollectionArticlesAction,
} from '../../redux/actions/collection-articles';
import initSpid from '../../utils/init-spid';
import GridTeaser from '../../local-components/GridTeaser';
import GenericError from '../error';
import generateCollectionRoute from '../../utils/generate-collection-route';
import redirectToCollectionByTitle from '../../utils/redirect-to-collection-by-title';
import clientParams from '../../tracking/client-params';
import { getFonts, fontsToInlineStyle } from '../../utils/font-chooser';
import { getArticleAdKeywords } from '../../ads/helpers';
import clientPlacedAds from './clientPlacedAds.json';

function mapDispatchToProps(dispatch) {
    return bindActionCreators({ addCollectionArticlesAction }, dispatch);
}
const ARTICLES_PER_PAGE = 21;

@provideHooks({
    /* eslint-disable arrow-body-style */
    fetch: async ({ params, dispatch, location, force }) => {
        const { sections } = config;
        const { section, subSection, tag, story, tagTitle } = params;
        const { offset = 0 } = location.query;

        let sectionId;
        if (section) {
            sectionId = sections[section];

            if (subSection) {
                sectionId = sections[`${section}/${subSection}`];
            }

            if (!sectionId || tagTitle) {
                // redirect /<section>/<tag-name> to correct tag url
                await redirectToCollectionByTitle({ dispatch, title: tagTitle || subSection });

                return; // triggers 404
            }

            params.section = sectionId; // eslint-disable-line no-param-reassign
        }

        if (sectionId !== '' || tag !== '' || story !== '') {
            await dispatch(
                fetchCollectionArticlesAction({ force, offset, section: sectionId, tag, story }),
            );
            await generateCollectionRoute(dispatch, location, params);
        }
    },
    /* eslint-enable arrow-body-style */
    defer: ({ dispatch, location, getProps }) => {
        dispatch(async (thunkDispatch, getState) => {
            const { layout } = getProps();
            const spidPromise = initSpid({ thunkDispatch, location, getState });

            await deferOnload();

            const trackingOpt = clientParams({
                config,
                layout,
                state: getState(),
                location,
            });

            const trackingPromise = thunkDispatch(
                trackingActions.trackerMultipleStart(trackingOpt),
            );

            await Promise.all([spidPromise, trackingPromise]);
        });
    },
})
@connect(
    (state) => ({
        articles: state.collectionArticles,
        device: state.device,
    }),
    mapDispatchToProps,
)
@themer('Collection')
export default class Collection extends Component {
    static propTypes = {
        theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        articles: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        params: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
        addCollectionArticlesAction: PropTypes.func.isRequired,
        device: PropTypes.string,
        location: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    };

    static defaultProps = {
        theme: {},
        articles: {},
        device: 'desktop',
        location: {},
    };

    constructor(props) {
        super(props);

        const { location, articles } = props;

        this.state = {
            offset: location.query.offset || 0,
            hasMoreArticles: articles.collection && articles.collection.length >= ARTICLES_PER_PAGE,
            isLoading: false,
        };

        this.scrollListener = throttle(this.fetchMoreArticlesListener, 500);
    }

    fetchMoreArticles = async () => {
        window.removeEventListener('scroll', this.scrollListener);

        const newOffset = this.state.offset + ARTICLES_PER_PAGE;
        const { section = '', tag = '', story = '' } = this.props.params;

        this.setState({
            offset: newOffset,
            isLoading: true,
        });
        const response = await this.props.addCollectionArticlesAction({
            section,
            tag,
            story,
            offset: newOffset,
        });

        // click tracking
        try {
            if (response.payload.articles && response.payload.articles.length > 1) {
                window.AutoTrack.activity()
                    .events.trackClick('fetch-more')
                    .send();
            }
        } catch (err) {
            console.error(err); // eslint-disable-line no-console
        }

        if (!response.payload.articles || response.payload.articles.length < ARTICLES_PER_PAGE) {
            this.setState({
                hasMoreArticles: false,
                isLoading: true,
            });

            return;
        }

        window.addEventListener('scroll', this.scrollListener);
    };

    fetchMoreArticlesListener = () => {
        const elementOffset = this.fetchMoreButton.offsetTop;
        if (window.scrollY + window.innerHeight * 1.5 < elementOffset) return; // eslint-disable-line no-mixed-operators

        this.fetchMoreArticles();
    };

    isSponsorateHandball() {
        const { articles = {} } = this.props;
        const { meta = {} } = articles;

        if (!meta.section || !meta.section.parent) {
            return false;
        }

        if (meta.section.id === '462cf64c-84bb-4e71-8fda-f6ce2d5ae3df') {
            return true;
        }

        return false;
    }

    render() {
        const { theme = {}, articles = {}, device = 'desktop', location } = this.props;
        const { meta = {} } = articles;
        const { offset, hasMoreArticles, isLoading } = this.state;
        const isMobile = device.indexOf('mobile') > -1;

        if (articles.error || !articles.collection.length) {
            const statusCode = articles.status || 404;
            return <GenericError statusCode={statusCode} theme={theme} />;
        }

        const collectionArticles = articles.collection.map((article, index) => (
            <GridTeaser key={article.id} article={article} theme={theme} index={index} />
        ));

        let collectionTitle = '';
        let bodyTheme = 'default';
        if (meta.tag) {
            collectionTitle = meta.tag.title;
        } else if (meta.story) {
            collectionTitle = meta.story.title;
        } else if (meta.section) {
            collectionTitle = meta.section.title;
            if (meta.section.parent && meta.section.parent.title === 'Sport') {
                bodyTheme = 'sport';
            }
        }

        const appnexusSponsorKeywords = [];
        if (this.isSponsorateHandball()) {
            appnexusSponsorKeywords.push({
                title: 'Håndball VM',
            });
        }

        let appnexusKeywords = {};
        if (meta.section) {
            appnexusKeywords = getArticleAdKeywords(
                {
                    ...meta,
                    tags: appnexusSponsorKeywords,
                },
                device,
            );
        }

        const { appnexus } = config;
        const fonts = getFonts(config.fonts);
        const fontPreloadLinks = fonts.map((font) => ({
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            href: `${font.cdn}.woff2`,
            crossOrigin: true,
        }));

        const links = [
            {
                rel: 'canonical',
                href: location.pathname,
            },
        ];

        if (hasMoreArticles) {
            links.push({
                rel: 'next',
                href: `${location.pathname}?offset=${offset + ARTICLES_PER_PAGE}`,
            });
        }

        return (
            <div className={theme.root}>
                <SponsorAd clientPlacedAds={clientPlacedAds} />
                <BodyClass theme={`vgTheme-${bodyTheme}`}>
                    <Helmet title={`${collectionTitle} – VG`} link={links.concat(fontPreloadLinks)}>
                        <meta
                            name="description"
                            content={
                                `Siste nytt om ${collectionTitle}.` +
                                ` Les og se nyheter, artikler og videoer om ${collectionTitle} på VG.no.`
                            }
                        />
                        <style type="text/css">{fontsToInlineStyle(fonts)}</style>
                    </Helmet>
                </BodyClass>
                {!isMobile && <HorseshoeAd clientPlacedAds={clientPlacedAds} device={device} />}
                <section className={theme.front}>
                    <PageOptions
                        member={appnexus.member}
                        disablePsa
                        noBidIfUnsold
                        keywords={appnexusKeywords}
                    />
                    <h1 className={theme.subheader}>
                        Siste saker om <span className={theme.tagTitle}>{collectionTitle}</span>
                    </h1>
                    <div className={theme.articleCards}>{collectionArticles}</div>
                    {hasMoreArticles && (
                        <button
                            ref={(element) => {
                                this.fetchMoreButton = element;
                            }}
                            className={theme.showMore}
                            onClick={this.fetchMoreArticles}
                            disabled={isLoading}
                        >
                            Vis flere artikler
                        </button>
                    )}
                </section>
            </div>
        );
    }
}



/** WEBPACK FOOTER **
 ** ./src/screens/front/collection.js
 **/